import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { Builder } from 'builder-pattern';
import { catchError, first, forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ModalConfirmationComponent } from 'src/app/components/modal-confirmation/modal-confirmation.component';
import { ParamsHelper } from 'src/app/helpers/ParamsHelper';
import { ResponseApiHelper } from 'src/app/helpers/response-api/response-api-helper';
import { OperationFilter } from 'src/app/pages/operation/shared/operation-filter';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { CarbonOperationRequest } from 'src/app/shared/interfaces/carbon-operation-request';
import { CarbonOperationType } from 'src/app/shared/interfaces/carbon-operation-type.enum';
import { InstrumentQuantityInfo } from 'src/app/shared/interfaces/instrument-quantity-info';
import { PositionCustodyResponse } from 'src/app/shared/interfaces/position-custody';
import { OperationApprove } from 'src/app/shared/interfaces/operation-approve';
import { OperationApproveStatus } from 'src/app/shared/interfaces/operation-approve-status.enum';
import { OperationStatus } from 'src/app/shared/interfaces/operation-status.enum';
import { OperationType } from 'src/app/shared/interfaces/operation-type.enum';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import { PatchApi, PostApi, ResponseApiSingle } from 'src/app/shared/interfaces/response-api';
import { AppModalService } from 'src/app/shared/services/app-modal.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { AppConfigService } from 'src/environments/app-config.service';
import { extractDocumentCharacters } from 'src/app/helpers/utils/cpf-cnpj.util';
import { HandleErrorService } from '../handle-error/handle-error.service';
import {
  FILTERABLE_OPERATION_TYPES,
} from 'src/app/pages/operation/shared/operation-type-code.utils';

@Injectable({
  providedIn: 'root'
})
export class OperationTransferenceService extends ResponseApiHelper<CarbonOperation> {

  private readonly environment: any
  private apiUrl: string;

  constructor(
    protected override readonly httpClient: HttpClient,
    private readonly appConfig: AppConfigService,
    private readonly modalService: AppModalService,
    private readonly b3ToastService: AppToastService,
    private readonly datePipe: DatePipe,
    private readonly handleErrorService: HandleErrorService
  ) {
    super(httpClient)
    this.environment = this.appConfig.config;
    this.apiUrl = `${this.environment.apiOperations}/operations`
   }

   public getTypeOptions(): B3SelectItem[] {
    return FILTERABLE_OPERATION_TYPES.map(type => ({
      label: this.transformOperationType(type) || type,
      inputId: type,
    }));
  }

  public transferenceRegister(form: CarbonOperationRequest, type: CarbonOperationType): Observable<CarbonOperation> {
    const transferenceData = this.transferenceBuilder(form, type);
    const data: PostApi<CarbonOperationRequest> = {
      data: transferenceData
    }
    return this.httpClient.post<ResponseApiSingle<CarbonOperation>>(this.apiUrl, data).pipe(
      first(),
      map((response) => {
        const carbonOp: CarbonOperation = response.data['operation'] || {};
        return carbonOp
      }),
      catchError(err => {
        return this.handleErrorService.handleError(err)
      })
    );
  }

  public transferenciaRetirada(form: CarbonOperationRequest, type: CarbonOperationType): Observable<CarbonOperation> {
    const transferenceData = this.transferenceBuilder(form, type);
    const data: PostApi<CarbonOperationRequest> = {
      data: transferenceData
    }
    return this.httpClient.post<ResponseApiSingle<CarbonOperation>>(this.apiUrl, data).pipe(
      first(),
      map((response) => {
        const carbonOp: CarbonOperation = response.data['operation'] || {};
        return carbonOp
      }),
    );
  }

  public approveOperation(operationApprove: OperationApprove): Observable<OperationApprove> {
    const data: PatchApi<OperationApprove> = {
      data: {
        operations: [operationApprove]
      }
    }

    return this.httpClient.patch<OperationApprove>(this.apiUrl, data).pipe(
      catchError((err) => {
        let text = ''
        if (operationApprove.statusCode === OperationApproveStatus.APPROVED) {
          text = 'Não foi possível aprovar a operação'
        } else if (operationApprove.statusCode === OperationApproveStatus.REJECTED) {
          text = 'Não foi possível rejeitar a operação'
        }
        this.b3ToastService.show({
          status: 'warning',
          text
        });

        return this.handleErrorService.handleError(err)
      })
    );
  }

  public getOperationPendingApproval(
    filters: OperationFilter,
    pendingApproval: boolean,
    pagination: Pagination<CarbonOperation>,
    justFinished = false,
  ): Observable<Pagination<CarbonOperation>> {
    let params = new ParamsHelper(new HttpParams())
    .setParams('page', (pagination.page + ''))
    .setParams('page-size', (pagination.size + ''))
    .setParams('initialDate', (filters.initialDate?.toString() || ''))
    .setParams('finalDate', (filters.finalDate?.toString() || ''))
    .setParams('holderCode', (filters.holderCode || ''))
    .setParams('purchaserCode', (filters.purchaserCode || ''))
    .setParams('carbonCreditCode', (filters.carbonCreditCode || ''))
    .setParams('operationCode', (filters.operationCode || ''))
    .setParams('typeCode', (filters.typeCode || ''))
    .setParams('pendingApprovalIndicator', pendingApproval)
    .setParams('justFinished', justFinished)
    .getParams();

    return this.genericGet(this.apiUrl, 'operations', params, pagination).pipe(
      map((response) => {
        response.results.forEach(
          (result) => {
            result.rawTypeCode = `${result.typeCode ?? ''}`;
            result.typeCode = this.transformOperationType(result.typeCode);
            result.statusCode = this.transformOperationStatus(result.statusCode);
            result.insertDate = this.datePipe.transform(result.insertDate, 'dd/MM/yyyy') || ''
          }
        )
        return response
      }),
      catchError((err) => {
        this.modalService.open(ModalConfirmationComponent, {
          modalTitleType: 'error',
          modalType: 'info',
          title: 'Erro.',
          content: 'Não foi possível carregar a tabela',
          closeButton: true,
        })

        return this.handleErrorService.handleError(err)
      })
    )
  }

  public hasOperationsInPeriod(initialDate: string, finalDate: string): Observable<boolean> {
    const params = new ParamsHelper(new HttpParams())
      .setParams('page', '1')
      .setParams('page-size', '1')
      .setParams('initialDate', initialDate)
      .setParams('finalDate', finalDate)
      .setParams('pendingApprovalIndicator', false)
      .setParams('justFinished', false)
      .getParams();

    const pagination: Pagination<CarbonOperation> = { page: 1, size: 1, results: [], total: 0 };

    return this.genericGet(this.apiUrl, 'operations', params, pagination).pipe(
      map(response => (response.total ?? 0) > 0),
      catchError(() => of(false)),
    );
  }

  private transferenceBuilder(form: CarbonOperationRequest, type: CarbonOperationType): CarbonOperationRequest {
    const cpfCnpjComitente = form?.cpfCnpjComitente
      ? extractDocumentCharacters(form.cpfCnpjComitente)
      : undefined;
    const purchaserComitenteDocument = form?.purchaserComitenteDocument
      ? extractDocumentCharacters(form.purchaserComitenteDocument)
      : undefined;

    return Builder<CarbonOperationRequest>()
      .creditQuantity(form?.creditQuantity)
      .carbonCreditCode(form?.carbonCreditCode)
      .holderCode(form?.holderCode)
      .purchaserCode(form?.purchaserCode)
      .typeCode(type)
      .withdrawalMode(form.withdrawalMode)
      .withdrawals(form.withdrawals)
      .cpfCnpjComitente(cpfCnpjComitente)
      .purchaserComitenteDocument(purchaserComitenteDocument)
      .acceptanceIndicator(form?.acceptanceIndicator)
      .investor({ documentNumber: cpfCnpjComitente || '' })
      .build()
  }

  private transformOperationType(type: string): string {
    switch (type) {
      case OperationType.DEPOSIT:
        return 'Depósito'
      case OperationType.TRANSFER_WITHOUT_FINANCIAL:
        return 'Transferência'
      case OperationType.WITHDRAW:
        return 'Retirada'
      case OperationType.DELIVERY:
        return 'Retirado da custódia da ACX'
      case OperationType.TRADE_FROM:
        return 'Operação de Venda'
      case OperationType.TRADE_TO:
        return 'Operação de Compra'
      case OperationType.RETIREMENT:
        return 'Aposentadoria'
      case OperationType.MINT:
        return 'MINT'
      case OperationType.TRADE:
        return 'Negociação'
      default:
        return ''
    }
  }

  private transformOperationStatus(status: string): string {
    switch (status) {
      case OperationStatus.OP_AG_APROV_ADQUIRENTE:
        return 'Pend. Aprovação'
      case OperationStatus.OP_AG_APROV_DETENTOR:
        return 'Pend. Aprovação'
      case OperationStatus.OP_AG_APROV_IF_B3:
        return 'Pend. Confirmação'
      case OperationStatus.OP_FINALIZADA:
        return 'Finalizada'
      case OperationStatus.OP_REJEITADA:
        return 'Rejeitada'
      case OperationStatus.OP_REJEITADA_POR_IF:
        return 'Rejeitada'
      case OperationStatus.TIMEOUT:
        return 'Cancelado: Timeout'
      default:
        return '';
    }
  }

  public getInstrumentQuantity(carbonCreditCode: string, holderCode: string): Observable<InstrumentQuantityInfo | null> {
    return forkJoin({
      custody: this.fetchCustodyQuantity(holderCode, carbonCreditCode),
      instrument: this.fetchInstrumentTotalQuantity(carbonCreditCode),
    }).pipe(
      first(),
      switchMap(({ custody, instrument }) => {
        if (custody != null && instrument != null) {
          return of({ available: custody, total: instrument });
        }

        return this.resolveInstrumentQuantityFallback(carbonCreditCode, holderCode, custody, instrument);
      }),
      catchError(() => this.resolveInstrumentQuantityFallback(carbonCreditCode, holderCode, null, null)),
    );
  }

  private resolveInstrumentQuantityFallback(
    carbonCreditCode: string,
    holderCode: string,
    custody: number | null,
    instrumentTotal: number | null,
  ): Observable<InstrumentQuantityInfo | null> {
    return this.fetchLegacyInstrumentQuantity(carbonCreditCode, holderCode).pipe(
      switchMap(legacy => {
        if (legacy != null) {
          return of(instrumentTotal != null
            ? { available: legacy.available, total: instrumentTotal }
            : legacy);
        }

        if (custody != null && instrumentTotal != null) {
          return of({ available: custody, total: instrumentTotal });
        }

        if (instrumentTotal != null) {
          return of({ available: custody ?? instrumentTotal, total: instrumentTotal });
        }

        return this.fetchIssuerInstrumentQuantity(carbonCreditCode);
      }),
    );
  }

  private fetchCustodyQuantity(accountCode: string, carbonCreditCode: string): Observable<number | null> {
    const account = this.sanitizeAccountCode(accountCode);
    const code = this.sanitizeInstrumentCode(carbonCreditCode);
    if (!account || !code) {
      return of(null);
    }

    const positionDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd') ?? '';
    const params = new HttpParams()
      .set('page', '1')
      .set('page-size', '1')
      .set('accountCode', account)
      .set('carbonCreditCode', code)
      .set('positionDate', positionDate);

    const custodyUrl = `${this.environment.apiCustody}/custodies`;

    return this.httpClient.get<PositionCustodyResponse>(custodyUrl, { params }).pipe(
      first(),
      map(response => {
        const creditQnty = response.data?.[0]?.creditQnty;
        return creditQnty == null ? null : Number(creditQnty);
      }),
      catchError(() => of(null)),
    );
  }

  private fetchInstrumentTotalQuantity(carbonCreditCode: string): Observable<number | null> {
    return this.fetchIssuerInstrumentQuantity(carbonCreditCode).pipe(
      map(info => info?.total ?? null),
    );
  }

  private fetchIssuerInstrumentQuantity(carbonCreditCode: string): Observable<InstrumentQuantityInfo | null> {
    const code = this.sanitizeInstrumentCode(carbonCreditCode);
    if (!code) {
      return of(null);
    }

  
    if (!/^[A-Za-z0-9_-]+$/.test(code)) {
      return of(null);
    }

    const safeInstrumentsUrl = `${this.environment.apiIssuer}/instruments/${encodeURIComponent(code)}`;

    return this.httpClient.get<{ data: Record<string, unknown> }>(safeInstrumentsUrl).pipe(
      first(),
      map(response => {
        const data = response.data;
        const total = data?.['creditInitialQuantity'] ?? data?.['creditQuantity'];
        const available = data?.['creditQuantity'] ?? total;

        if (available == null || total == null) {
          return null;
        }

        return {
          available: Number(available),
          total: Number(total),
        };
      }),
      catchError(() => of(null)),
    );
  }

  private fetchLegacyInstrumentQuantity(
    carbonCreditCode: string,
    holderCode: string,
  ): Observable<InstrumentQuantityInfo | null> {
    const code = this.sanitizeInstrumentCode(carbonCreditCode);
    const holder = this.sanitizeAccountCode(holderCode);
    if (!code || !holder) {
      return of(null);
    }

    const params = new HttpParams()
      .set('carbonCreditCode', code)
      .set('holderCode', holder);

    const instrumentQuantityUrl = `${this.apiUrl}/instrument-quantity`;

    return this.httpClient.get<ResponseApiSingle<Record<string, unknown>>>(
      instrumentQuantityUrl,
      { params },
    ).pipe(
      first(),
      map((response) => this.parseLegacyInstrumentQuantity(response.data)),
      catchError(() => of(null)),
    );
  }

 
  private sanitizeInstrumentCode(value: string): string | null {
    const code = (value ?? '').trim();
    return /^[A-Za-z0-9]+$/.test(code) ? code : null;
  }

 
  private sanitizeAccountCode(value: string): string | null {
    const code = (value ?? '').trim();
    return /^[A-Za-z0-9._-]+$/.test(code) ? code : null;
  }

  private parseLegacyInstrumentQuantity(data: Record<string, unknown> | undefined): InstrumentQuantityInfo | null {
    if (!data) {
      return null;
    }

    const quantity = data['quantity'];
    if (typeof quantity === 'string' && quantity.includes('/')) {
      const [available, total] = quantity.split('/');
      if (available?.trim() && total?.trim()) {
        return {
          available: Number(available.trim()),
          total: Number(total.trim()),
        };
      }
    }

    const available = data['availableQuantity'] ?? data['creditQnty'] ?? data['available'];
    const total = data['totalQuantity'] ?? data['total'];
    if (available != null && total != null) {
      return {
        available: Number(available),
        total: Number(total),
      };
    }

    return null;
  }

}
